/**
 * Testaferros gap-detection — compares the declared ownership graph (owners.ts /
 * ownership.ts's effective-ownership math) against the directores/dignatarios/
 * apoderados synced from the Registro Público (`registry_officers`, via
 * `api.listRegistryOfficers`) to surface two kinds of mismatch:
 *
 *   - An officer (persona con firma ante el Registro Público) who does not appear
 *     among the declared effective UBOs — a possible testaferro (nominee) situation.
 *   - An effective UBO who does not appear as an officer in the Registro Público —
 *     the declared owner has no visible signing authority on record.
 *
 * Matching is by normalized name equality (accent/case/punctuation-insensitive) —
 * there is no stable shared identifier between `subject_owners` and
 * `registry_officers`.
 */
import type { EntityMode, RegistryOfficerResponse } from '@cumpli/shared';
import { isUboByOwnership } from '@cumpli/shared';
import { effectiveOwnership } from './ownership';
import type { OwnerUi } from './api';

/** Accent-fold, lowercase, collapse non-alphanumeric to single spaces, trim. */
export function normalizarNombre(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export interface OficialConBrecha extends RegistryOfficerResponse {
  /** true si esta persona firma ante el Registro Público pero no figura como dueño efectivo declarado. */
  firmaSinDueno: boolean;
}

export interface BrechasTestaferros {
  oficiales: OficialConBrecha[];
  /** Dueños efectivos (UBO) declarados que no figuran entre los directivos del Registro Público. */
  duenosSinFirma: OwnerUi[];
}

export function detectarBrechasTestaferros(
  owners: readonly OwnerUi[],
  oficiales: readonly RegistryOfficerResponse[],
  sector: EntityMode
): BrechasTestaferros {
  const eff = effectiveOwnership(
    owners.map((o) => ({
      id: o.id,
      parentOwnerId: o.parentOwnerId,
      ownershipPct: o.ownershipPct,
    }))
  );
  const duenosEfectivos = owners.filter(
    (o) => o.ownerType === 'natural' && isUboByOwnership(eff.get(o.id) ?? 0, sector)
  );
  const setDuenos = new Set(duenosEfectivos.map((o) => normalizarNombre(o.name)));
  const setOficiales = new Set(oficiales.map((o) => normalizarNombre(o.nombre)));

  return {
    oficiales: oficiales.map((o) => ({
      ...o,
      firmaSinDueno: !setDuenos.has(normalizarNombre(o.nombre)),
    })),
    duenosSinFirma: duenosEfectivos.filter((o) => !setOficiales.has(normalizarNombre(o.name))),
  };
}
